/**
 * Read-only platform-administrator member list for one Cognitory system.
 */

import { useState } from "react";
import { usePlatformAdministratorMembers } from "../entry/usePlatformAdministratorMembers";
import {
  MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
} from "../theme/mainPageVisual";

function memberPhaseLabel(
  phase: ReturnType<typeof usePlatformAdministratorMembers>["phase"],
): string {
  switch (phase) {
    case "loading":
      return "Loading members…";
    case "listed":
      return "Members";
    case "empty":
      return "No members in this Cognitory.";
    case "truncated":
      return "Members (list truncated)";
    case "forbidden":
      return "You cannot view members for this Cognitory.";
    case "unauthenticated":
      return "Sign in to view members for this Cognitory.";
    case "invalid_request":
      return "This member list request could not be accepted.";
    case "temporary_failure":
      return "Unable to load members right now.";
    case "unable_to_verify":
      return "Unable to verify the member list.";
    default:
      return "";
  }
}

export default function PlatformAdministratorMembersPanel(input: {
  systemId: string;
}) {
  const [open, setOpen] = useState(false);
  const membersState = usePlatformAdministratorMembers({
    systemId: input.systemId,
    enabled: open,
  });

  if (!open) {
    return (
      <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
        <button
          type="button"
          data-testid="platform-admin-members-open"
          data-system-id={input.systemId}
          style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
          onClick={() => setOpen(true)}
        >
          View members
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="platform-admin-members-panel"
      data-system-id={input.systemId}
      data-fetch-count={String(membersState.fetchCount)}
      style={{
        marginTop: "0.75rem",
        padding: "0.85rem",
        borderRadius: MAIN_PAGE_RADIUS.panel,
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: "rgba(255, 255, 255, 0.55)",
      }}
    >
      <p
        data-testid="platform-admin-members-status"
        style={{
          margin: "0 0 0.65rem",
          fontSize: "0.9rem",
          color: MAIN_PAGE_COLORS.slate,
          fontFamily: MAIN_PAGE_FONT_STACK,
          textAlign: "center",
        }}
      >
        {memberPhaseLabel(membersState.phase)}
      </p>

      {membersState.requestId &&
      (membersState.phase === "temporary_failure" ||
        membersState.phase === "unable_to_verify" ||
        membersState.phase === "forbidden" ||
        membersState.phase === "unauthenticated" ||
        membersState.phase === "invalid_request") ? (
        <p
          data-testid="platform-admin-members-request-id"
          style={{
            textAlign: "center",
            fontSize: "0.78rem",
            color: MAIN_PAGE_COLORS.whisper,
            fontFamily: MAIN_PAGE_FONT_STACK,
            marginTop: 0,
          }}
        >
          Request ID: {membersState.requestId}
        </p>
      ) : null}

      {membersState.phase === "listed" || membersState.phase === "truncated" ? (
        <ul
          data-testid="platform-admin-members-list"
          style={{
            listStyle: "none",
            padding: 0,
            margin: "0.65rem 0 0",
            display: "grid",
            gap: "0.55rem",
          }}
        >
          {membersState.members.map((member, index) => (
            <li
              key={`${member.createdAt}-${member.role}-${index}`}
              data-testid="platform-admin-member-row"
              style={{
                padding: "0.55rem 0.65rem",
                borderRadius: MAIN_PAGE_RADIUS.alert,
                border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
                background: "rgba(255, 255, 255, 0.72)",
                fontSize: "0.85rem",
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              <div>Email: {member.email ?? "—"}</div>
              <div>Display name: {member.displayName ?? "—"}</div>
              <div>Role: {member.role}</div>
              <div>Membership: {member.status}</div>
              <div>Account: {member.accountState}</div>
              <div>Added: {member.createdAt}</div>
            </li>
          ))}
        </ul>
      ) : null}

      <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
        <button
          type="button"
          data-testid="platform-admin-members-close"
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
}
